/**
 * Customer checkout link mock operations
 */

import { mockCustomers } from './base';

type CheckoutLinkStatus = 'active' | 'expired' | 'consumed';

interface CheckoutLinkRecord {
  id: string;
  product_id: string;
  plan_id: string | null;
  package_id: string | null;
  cycle_id: string | null;
  price_id: string | null;
  currency: string | null;
  status: CheckoutLinkStatus;
  created_at: string;
  expires_at: string | null;
  used_at: string | null;
}

const checkoutLinkOptions = {
  products: [
    { id: '1', name: 'Weather API' },
    { id: '2', name: 'Email Automation API' },
    { id: '3', name: 'SMS Gateway' },
  ],
  plans: [
    { id: 'plan-1', name: 'Starter', productId: '1', isVariant: false, parentPlanId: null },
    { id: 'plan-2', name: 'Pro', productId: '1', isVariant: false, parentPlanId: null },
    { id: 'plan-3', name: 'Growth', productId: '2', isVariant: false, parentPlanId: null },
  ],
  packages: [
    { id: 'package-1', name: 'Credits 10k', productId: '3' },
    { id: 'package-2', name: 'Credits 50k', productId: '3' },
  ],
  prices: [
    { id: 'price-1', planId: 'plan-1', packageId: null, cycleId: 'cycle-1', currency: 'USD', amount: 29 },
    { id: 'price-2', planId: 'plan-2', packageId: null, cycleId: 'cycle-1', currency: 'USD', amount: 79 },
    { id: 'price-3', planId: 'plan-3', packageId: null, cycleId: 'cycle-2', currency: 'USD', amount: 59 },
    { id: 'price-4', planId: null, packageId: 'package-1', cycleId: null, currency: 'USD', amount: 99 },
    { id: 'price-5', planId: null, packageId: 'package-2', cycleId: null, currency: 'USD', amount: 199 },
  ],
  cycles: [
    { id: 'cycle-1', productId: '1', name: 'Monthly', months: 1 },
    { id: 'cycle-2', productId: '2', name: 'Annual', months: 12 },
  ],
};

const checkoutLinksByCustomer: Record<string, CheckoutLinkRecord[]> = {
  'customer-uuid-1': [
    {
      id: 'link-uuid-1',
      product_id: 'product-uuid-1',
      plan_id: 'plan-uuid-1',
      package_id: null,
      cycle_id: 'cycle-uuid-1',
      price_id: 'price-uuid-1',
      currency: 'USD',
      status: 'active',
      created_at: '2026-01-26T13:20:07Z',
      expires_at: '2026-01-26T13:44:07Z',
      used_at: null,
    },
  ],
};

const ensureCustomer = (customerId: string) => {
  const exists = mockCustomers.some((customer) => customer.id === customerId);
  if (!exists) {
    throw {
      response: {
        status: 404,
        data: {
          message: 'Customer not found',
        },
      },
    };
  }
};

export const checkoutLinkOperations = {
  list: (customerId: string) => {
    ensureCustomer(customerId);
    return {
      data: {
        checkout_links: checkoutLinksByCustomer[customerId] ?? [],
      },
      jsonapi: { version: '1.0' },
    };
  },
  create: (
    customerId: string,
    data: {
      product_id: string;
      plan_id?: string | null;
      package_id?: string | null;
      cycle_id?: string | null;
      price_id?: string | null;
      currency?: string | null;
      expires_at?: string | null;
      expiresAt?: string | null;
    },
  ) => {
    ensureCustomer(customerId);

    const now = new Date();
    const defaultExpiry = new Date(now.getTime() + 15 * 60 * 1000).toISOString();
    const requestedExpiry = data.expires_at ?? data.expiresAt;
    const parsedExpiry =
      typeof requestedExpiry === 'string' ? new Date(requestedExpiry) : null;
    const expiresAt =
      parsedExpiry && !Number.isNaN(parsedExpiry.getTime())
        ? parsedExpiry.toISOString()
        : defaultExpiry;
    const token = `checkout-token-${Date.now()}`;
    const record: CheckoutLinkRecord = {
      id: `link-${Date.now()}`,
      product_id: data.product_id,
      plan_id: data.plan_id ?? null,
      package_id: data.package_id ?? null,
      cycle_id: data.cycle_id ?? null,
      price_id: data.price_id ?? null,
      currency: data.currency ?? 'USD',
      status: 'active',
      created_at: now.toISOString(),
      expires_at: expiresAt,
      used_at: null,
    };

    checkoutLinksByCustomer[customerId] = [
      record,
      ...(checkoutLinksByCustomer[customerId] ?? []),
    ];

    return {
      data: {
        checkout_url: `https://checkout.example.com/products/${data.product_id}/checkout?checkout_token=${token}`,
        token,
        expires_at: expiresAt,
      },
      jsonapi: { version: '1.0' },
    };
  },
  options: (customerId: string) => {
    ensureCustomer(customerId);
    return {
      data: checkoutLinkOptions,
      jsonapi: { version: '1.0' },
    };
  },
};
